/* ============================================================
   BNE STUDIO — Midnight Blueprint motion engine (React hook)
   Ported from the design handoff (night.js): scroll reveals,
   count-ups, nav dropdowns, mobile sheet, parallax, pointer
   tilt, spotlight glow, nav shadow. All queries are scoped to
   the page root ref and every listener/observer is cleaned up
   on unmount.
   ============================================================ */
import { useEffect } from "react";

export default function useNightMotion(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const cleanups = [];
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function canScroll() {
      const de = document.scrollingElement || document.documentElement;
      if (de.scrollHeight <= window.innerHeight + 4) return false;
      const y0 = window.scrollY;
      window.scrollTo(0, y0 + 4);
      const moved = window.scrollY !== y0;
      window.scrollTo(0, y0);
      return moved;
    }
    const SCROLLABLE = !reduce && canScroll();

    /* ---------- scroll reveal ---------- */
    (function initReveal() {
      const els = [].slice.call(root.querySelectorAll(".reveal, .reveal-scale"));
      if (!els.length) return;
      if (reduce || !("IntersectionObserver" in window) || !SCROLLABLE) {
        root.classList.add("reveal-off");
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              en.target.classList.add("in");
              io.unobserve(en.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((e) => io.observe(e));
      cleanups.push(() => io.disconnect());
      const t = setTimeout(() => {
        const stuck = els.some((e) => !e.classList.contains("in"));
        if (stuck) root.classList.add("reveal-off");
      }, 2600);
      cleanups.push(() => clearTimeout(t));
    })();

    /* ---------- count-up ---------- */
    (function initCounts() {
      const els = [].slice.call(root.querySelectorAll("[data-count]"));
      if (!els.length) return;
      function run(el) {
        if (el.dataset.counted) return;
        el.dataset.counted = "1";
        const target = parseFloat(el.getAttribute("data-count"));
        const suffix = el.getAttribute("data-suffix") || "";
        const dec = el.getAttribute("data-dec") | 0;
        const comma = el.hasAttribute("data-comma");
        function fmt(v) {
          let s = dec ? v.toFixed(dec) : Math.round(v).toString();
          if (comma) s = parseFloat(s).toLocaleString("en-US", { maximumFractionDigits: dec });
          return s + suffix;
        }
        if (reduce || !SCROLLABLE) {
          el.textContent = fmt(target);
          return;
        }
        let t0 = null;
        const dur = 1400;
        function step(ts) {
          if (!t0) t0 = ts;
          const p = Math.min((ts - t0) / dur, 1);
          el.textContent = fmt(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      }
      if (!SCROLLABLE || reduce) {
        els.forEach(run);
        return;
      }
      if ("IntersectionObserver" in window) {
        const io = new IntersectionObserver(
          (ents) => {
            ents.forEach((en) => {
              if (en.isIntersecting) {
                run(en.target);
                io.unobserve(en.target);
              }
            });
          },
          { threshold: 0.35 }
        );
        els.forEach((e) => io.observe(e));
        cleanups.push(() => io.disconnect());
      }
      const t = setTimeout(() => els.forEach(run), 2400);
      cleanups.push(() => clearTimeout(t));
    })();

    /* ---------- parallax (scroll) ---------- */
    (function initParallax() {
      if (reduce) return;
      const els = [].slice.call(root.querySelectorAll("[data-parallax]"));
      if (!els.length) return;
      let ticking = false;
      function update() {
        const vh = window.innerHeight;
        els.forEach((el) => {
          const speed = parseFloat(el.getAttribute("data-parallax")) || 0.1;
          const r = el.getBoundingClientRect();
          const center = r.top + r.height / 2;
          const off = (center - vh / 2) / vh;
          el.style.transform = "translate3d(0," + (off * speed * -100).toFixed(2) + "px,0)";
        });
        ticking = false;
      }
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      update();
      cleanups.push(() => window.removeEventListener("scroll", onScroll));
    })();

    /* ---------- mouse-tracked tilt on a scene ---------- */
    (function initPointer() {
      if (reduce) return;
      const stages = [].slice.call(root.querySelectorAll("[data-pointer]"));
      stages.forEach((scene) => {
        const layers = [].slice.call(scene.querySelectorAll("[data-depth]"));
        const onMove = (e) => {
          const r = scene.getBoundingClientRect();
          const mx = (e.clientX - r.left) / r.width - 0.5;
          const my = (e.clientY - r.top) / r.height - 0.5;
          layers.forEach((l) => {
            const d = parseFloat(l.getAttribute("data-depth")) || 0;
            l.style.transform =
              "translate3d(" + (mx * d * 26).toFixed(1) + "px," + (my * d * 26).toFixed(1) + "px,0)";
          });
        };
        const onLeave = () => {
          layers.forEach((l) => {
            l.style.transform = "translate3d(0,0,0)";
          });
        };
        scene.addEventListener("pointermove", onMove);
        scene.addEventListener("pointerleave", onLeave);
        cleanups.push(() => {
          scene.removeEventListener("pointermove", onMove);
          scene.removeEventListener("pointerleave", onLeave);
        });
      });
    })();

    /* ---------- glow follows cursor on .spotlight ---------- */
    (function initSpotlight() {
      if (reduce) return;
      [].slice.call(root.querySelectorAll(".spotlight")).forEach((el) => {
        const onMove = (e) => {
          const r = el.getBoundingClientRect();
          el.style.setProperty("--mx", (((e.clientX - r.left) / r.width) * 100).toFixed(1) + "%");
          el.style.setProperty("--my", (((e.clientY - r.top) / r.height) * 100).toFixed(1) + "%");
        };
        el.addEventListener("pointermove", onMove);
        cleanups.push(() => el.removeEventListener("pointermove", onMove));
      });
    })();

    /* ---------- nav dropdowns ---------- */
    (function initNav() {
      const items = [].slice.call(root.querySelectorAll(".nav-item"));
      // keep each dropdown button's aria-expanded in sync with its .open state
      const syncAria = () =>
        items.forEach((o) => {
          const b = o.querySelector("button");
          if (b) b.setAttribute("aria-expanded", o.classList.contains("open") ? "true" : "false");
        });
      items.forEach((it) => {
        const btn = it.querySelector("button");
        if (!btn) return;
        const onEnter = () => {
          items.forEach((o) => {
            if (o !== it) o.classList.remove("open");
          });
          it.classList.add("open");
          syncAria();
        };
        const onLeave = () => {
          it.classList.remove("open");
          syncAria();
        };
        const onClick = (e) => {
          e.stopPropagation();
          it.classList.toggle("open");
          syncAria();
        };
        it.addEventListener("mouseenter", onEnter);
        it.addEventListener("mouseleave", onLeave);
        btn.addEventListener("click", onClick);
        cleanups.push(() => {
          it.removeEventListener("mouseenter", onEnter);
          it.removeEventListener("mouseleave", onLeave);
          btn.removeEventListener("click", onClick);
        });
      });
      const onDoc = () => {
        items.forEach((o) => o.classList.remove("open"));
        syncAria();
      };
      document.addEventListener("click", onDoc);
      cleanups.push(() => document.removeEventListener("click", onDoc));
    })();

    /* ---------- mobile sheet ---------- */
    (function initMobile() {
      const burger = root.querySelector("#burger");
      const sheet = root.querySelector("#msheet");
      if (!burger || !sheet) return;
      const closeBtn = sheet.querySelector(".msheet-close");
      const openSheet = () => {
        sheet.classList.add("open");
        document.body.style.overflow = "hidden";
      };
      const closeSheet = () => {
        sheet.classList.remove("open");
        document.body.style.overflow = "";
      };
      burger.addEventListener("click", openSheet);
      cleanups.push(() => burger.removeEventListener("click", openSheet));
      if (closeBtn) {
        closeBtn.addEventListener("click", closeSheet);
        cleanups.push(() => closeBtn.removeEventListener("click", closeSheet));
      }
      [].slice.call(sheet.querySelectorAll("a")).forEach((a) => {
        a.addEventListener("click", closeSheet);
        cleanups.push(() => a.removeEventListener("click", closeSheet));
      });
      // release the scroll lock if we unmount while the sheet is open
      cleanups.push(() => {
        document.body.style.overflow = "";
      });
    })();

    /* ---------- nav shadow on scroll ---------- */
    (function initNavScroll() {
      const nav = root.querySelector(".nav");
      if (!nav) return;
      const on = () => {
        nav.style.boxShadow = window.scrollY > 12 ? "0 18px 40px rgba(2,3,9,.5)" : "none";
      };
      window.addEventListener("scroll", on, { passive: true });
      on();
      cleanups.push(() => window.removeEventListener("scroll", on));
    })();

    return () => cleanups.forEach((fn) => fn());
  }, [rootRef]);
}
